/*
 * Product card video previews (collection grids).
 * Cards whose second media is a video carry <span data-hover-video data-preview data-handle data-full>
 * (snippets/product-item.liquid). data-preview is a small looping clip (theme asset, ~0.3-0.5 MB) that starts almost
 * instantly; if it fails we fall back to the product's own renditions via /products/<handle>.js.
 * Nothing is downloaded on page load.
 *  - First card of the collection grid autoplays after load while on screen (touch + desktop).
 *  - Touch: the video card your finger lands on (to scroll or tap) starts playing at once and the previous one stops.
 *    Cards you only scroll past don't play. Taps still open the product. A card stops when it leaves the screen.
 *  - Desktop: any video card plays while hovered; the first card pauses meanwhile and resumes after.
 * Only one video plays at a time. Skipped for reduced motion, data saver and slow (2g/3g) connections.
 */
(function () {
  if (window.__nycHoverVideo) return;
  window.__nycHoverVideo = true;

  var conn = navigator.connection || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var slowNet = conn.saveData || /(^|-)(2g|3g)$/.test(conn.effectiveType || '');
  if (reduceMotion || slowNet || !('IntersectionObserver' in window)) return;

  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var WANT = canHover && window.devicePixelRatio > 1.5 ? 720 : 480;
  var fallbackCache = {};
  var active = null;

  function hasVideo(media) { return media && media.querySelector('[data-hover-video]'); }

  function fallbackSrc(data) {
    var handle = data.getAttribute('data-handle');
    var full = data.getAttribute('data-full');
    if (!fallbackCache[handle]) {
      fallbackCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          var video = p && p.media && p.media.filter(function (m) { return m.media_type === 'video'; })[0];
          var mp4 = video ? video.sources.filter(function (s) { return s.format === 'mp4'; }) : [];
          var best = mp4.filter(function (s) { return s.height === WANT; })[0] ||
                     mp4.filter(function (s) { return s.height <= 720; }).sort(function (a, b) { return b.height - a.height; })[0];
          return best ? best.url : full;
        })
        .catch(function () { return full; });
    }
    return fallbackCache[handle];
  }

  function play(media) {
    if (active !== media || media.__hvVideo) return;
    var data = media.querySelector('[data-hover-video]');
    var slot = (canHover && media.__hvHover && media.querySelector('.product-item__image--two')) || media.querySelector('.product-item__image--one') || media;
    var v = document.createElement('video');
    v.muted = true;
    v.loop = true;
    v.playsInline = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('aria-hidden', 'true');
    v.preload = 'auto';
    v.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:0;transition:opacity .2s ease;pointer-events:none;z-index:1;';
    v.addEventListener('playing', function () { v.style.opacity = '1'; }, { once: true });
    v.addEventListener('error', function () {   // preview clip missing: use the product's own video instead
      if (v.__fellBack) return;
      v.__fellBack = true;
      fallbackSrc(data).then(function (src) { if (media.__hvVideo === v && src) { v.src = src; v.play().catch(function () {}); } });
    });
    v.src = data.getAttribute('data-preview') || data.getAttribute('data-full');
    slot.appendChild(v);
    media.__hvVideo = v;
    var p = v.play();
    if (p && p.catch) p.catch(function (err) { if (err && err.name === 'NotAllowedError') stop(media); }); // e.g. iOS Low Power Mode
  }

  function start(media, delay) {
    if (active === media) return;
    if (active) stop(active);
    active = media;
    clearTimeout(media.__hvTimer);
    media.__hvTimer = setTimeout(function () { media.__hvTimer = null; play(media); }, delay);
  }

  function stop(media) {
    if (active === media) active = null;
    clearTimeout(media.__hvTimer);
    media.__hvTimer = null;
    var v = media.__hvVideo;
    if (!v) return;
    media.__hvVideo = null;
    v.pause();
    v.removeAttribute('src'); // stop any further downloading
    v.load();
    v.remove();
  }

  // ---------------- First card of the collection grid: autoplay while on screen (touch + desktop) ----------------
  var firstCard = null, firstVisible = false;
  function resumeFirst() { if (firstCard && firstVisible && !active && !document.hidden) start(firstCard, 150); }
  function initFirst() {
    var grid = document.querySelector('.collection__window');
    var first = grid && grid.querySelector('.product-item .product-item__media');
    if (!hasVideo(first)) return;
    firstCard = first;
    new IntersectionObserver(function (entries) {
      firstVisible = entries[0].intersectionRatio >= 0.5;
      if (firstVisible) resumeFirst(); else if (active === firstCard) stop(firstCard);
    }, { threshold: [0, 0.5] }).observe(firstCard);
  }
  function onLoaded(fn) { if (document.readyState === 'complete') setTimeout(fn, 600); else window.addEventListener('load', function () { setTimeout(fn, 600); }, { once: true }); }
  onLoaded(initFirst);
  document.addEventListener('visibilitychange', function () { if (document.hidden) { if (active) stop(active); } else resumeFirst(); });

  // ---------------- Touch: the card your finger lands on plays ----------------
  if (!canHover) {
    // a playing card (other than the first) stops once it is scrolled fully off screen; the first card then resumes
    var offscreen = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (!e.isIntersecting && e.target === active) { stop(e.target); resumeFirst(); } });
    });
    window.addEventListener('touchstart', function (e) {
      var media = e.target.closest && e.target.closest('.product-item__media');
      if (!hasVideo(media) || active === media) return;
      if (!media.__hvWatched) { media.__hvWatched = true; offscreen.observe(media); }
      start(media, 0); // passive listener: scrolling and tapping work exactly as before
    }, { capture: true, passive: true });
    return;
  }

  // ---------------- Desktop: first card autoplays, hover plays any card ----------------
  document.addEventListener('pointerover', function (e) {
    var media = e.target.closest && e.target.closest('.product-item__media');
    if (!hasVideo(media) || media.__hvHover) return;
    media.__hvHover = true;
    if (active !== media) start(media, 120);
  });
  document.addEventListener('pointerout', function (e) {
    var media = e.target.closest && e.target.closest('.product-item__media');
    if (!media || !media.__hvHover || (e.relatedTarget && media.contains(e.relatedTarget))) return;
    media.__hvHover = false;
    if (media === firstCard) return;
    stop(media);
    resumeFirst();
  });
})();
